import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import PredictInput from '../components/PredictInput'
import { useAuth } from '../lib/auth'

/**
 * The boss fight.
 *
 * The client renders a timer and an HP bar, but owns neither. The clock it
 * shows is a countdown to a deadline the server recorded; the damage it
 * animates is recomputed server-side at submit. Pausing the tab, editing
 * state, or replaying the request all produce the same grade.
 */

interface Question {
  kind: string
  id: string
  prompt: string
  lessonTitle: string
  choices: { id: string; text: string }[]
}

interface Status {
  module: { id: string; slug: string; zone: string; boss: string; color: string }
  eligible: boolean
  reason: 'ok' | 'lessons_incomplete' | 'cooldown' | 'no_questions'
  lessonsDone: number
  lessonsTotal: number
  cooldownUntil: number | null
  bestHpLeft: number | null
  defeated: boolean
}

interface Fight {
  sessionId: string
  boss: string
  zone: string
  hpMax: number
  expiresAt: number
  durationMs: number
  questions: Question[]
}

interface Result {
  passed: boolean
  score: number
  total: number
  damage: number
  hpLeft: number
  hpMax: number
  maxCombo: number
  expired: boolean
  xpGranted: number
  artifact: { name: string; rarity: string; flavor_text: string } | null
  feedback: { questionId: string; correct: boolean; correctChoiceIds: string[]; explanation: string }[]
  game: { level: number; totalXp: number; progress: number }
}

export default function Boss() {
  const { slug } = useParams()
  const { user, game, setGame } = useAuth()
  const [status, setStatus] = useState<Status | null>(null)
  const [fight, setFight] = useState<Fight | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [selected, setSelected] = useState<Record<string, string[]>>({})
  // Predict answers are free text and need their own store, exactly as in
  // Quiz.tsx. Keeping them separate lets the submit builder tell the two kinds
  // apart without inspecting shapes.
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [remaining, setRemaining] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submittedRef = useRef(false)

  const loadStatus = useCallback(() => {
    api<Status>(`/game/boss/${slug}`).then(setStatus).catch(() => {})
  }, [slug])

  useEffect(() => {
    if (user) loadStatus()
  }, [user, loadStatus])

  const submit = useCallback(
    async (auto = false) => {
      if (!fight || submittedRef.current) return
      submittedRef.current = true
      setBusy(true)
      try {
        // Built from the FROZEN question list rather than from the answer
        // stores: iterating the stores would omit unanswered questions, and an
        // omitted question is one the server never grades — which on a boss
        // fight means it deals no damage but also costs nothing.
        const answers = fight.questions.map((q) =>
          q.kind === 'predict'
            ? { questionId: q.id, text: typed[q.id] ?? '' }
            : { questionId: q.id, choiceIds: selected[q.id] ?? [] },
        )
        const res = await api<Result>(`/game/boss/${slug}/submit`, {
          method: 'POST',
          body: { sessionId: fight.sessionId, answers },
        })
        setResult(res)
        setFight(null)
        if (game) setGame({ ...game, ...res.game })
        loadStatus()
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not submit the fight.')
        submittedRef.current = false
      } finally {
        setBusy(false)
        if (auto) setRemaining(0)
      }
    },
    [fight, selected, typed, slug, game, setGame, loadStatus],
  )

  // The countdown. When it hits zero we submit whatever exists — the server
  // would reject a late submission anyway, so failing to send is strictly worse
  // for the player than sending an incomplete one.
  useEffect(() => {
    if (!fight) return
    const tick = () => {
      const left = Math.max(0, fight.expiresAt - Date.now())
      setRemaining(left)
      if (left === 0) void submit(true)
    }
    tick()
    const timer = setInterval(tick, 250)
    return () => clearInterval(timer)
  }, [fight, submit])

  async function start() {
    setBusy(true)
    setError(null)
    setResult(null)
    setSelected({})
    // Must be cleared alongside `selected`. Leaving it behind means a second
    // fight starts pre-filled with the previous fight's typed answers, which
    // would silently deal damage for questions the player never looked at.
    setTyped({})
    submittedRef.current = false
    try {
      setFight(await api<Fight>(`/game/boss/${slug}/start`, { method: 'POST' }))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start the fight.')
    } finally {
      setBusy(false)
    }
  }

  if (!user) {
    return (
      <div className="boss-page">
        <p className="notice">Sign in to challenge a boss.</p>
        <Link to="/login" className="button button-primary">Sign in</Link>
      </div>
    )
  }

  if (!status) return <div className="loading">Approaching the boss…</div>

  // ── In-fight ──────────────────────────────────────────────────────────────
  if (fight) {
    // Count both kinds. Before this, a fight of predict questions showed
    // "0 of 8 answered" no matter how much the player had typed.
    const answered = fight.questions.filter((q) =>
      q.kind === 'predict'
        ? (typed[q.id] ?? '').trim().length > 0
        : (selected[q.id]?.length ?? 0) > 0,
    ).length
    const projected = estimateDamage(fight.questions, selected, typed)
    const seconds = Math.ceil(remaining / 1000)
    const urgent = seconds <= 60

    return (
      <div className="boss-page boss-active">
        <header className="boss-header">
          <h1>⚔ {fight.boss}</h1>
          <div className={urgent ? 'boss-timer boss-timer-urgent' : 'boss-timer'}>
            {String(Math.floor(seconds / 60)).padStart(2, '0')}:
            {String(seconds % 60).padStart(2, '0')}
          </div>
        </header>

        <div className="hp-bar" role="progressbar" aria-valuenow={fight.hpMax - projected} aria-valuemin={0} aria-valuemax={fight.hpMax}>
          <div className="hp-fill" style={{ width: `${Math.max(0, 100 - (projected / fight.hpMax) * 100)}%` }} />
          <span className="hp-text">
            {Math.max(0, fight.hpMax - projected)} / {fight.hpMax} HP
          </span>
        </div>
        <p className="dim boss-hint">
          {answered} of {fight.questions.length} answered. Consecutive correct answers multiply
          your damage — the combo is where the fight is won.
        </p>

        <ol className="questions">
          {fight.questions.map((q, i) => (
            <li key={q.id} className="question">
              <p className="question-prompt">
                <span className="dim">{i + 1}.</span> {q.prompt}
              </p>
              <p className="dim question-source">from “{q.lessonTitle}”</p>
              {q.kind === 'predict' ? (
                <PredictInput
                  id={q.id}
                  value={typed[q.id] ?? ''}
                  disabled={false}
                  onChange={(next) => setTyped((p) => ({ ...p, [q.id]: next }))}
                />
              ) : (
                <div className="choices" role="radiogroup" aria-label={q.prompt}>
                  {q.choices.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      role="radio"
                      aria-checked={selected[q.id]?.includes(ch.id) ?? false}
                      className={selected[q.id]?.includes(ch.id) ? 'choice choice-selected' : 'choice'}
                      onClick={() => setSelected((p) => ({ ...p, [q.id]: [ch.id] }))}
                    >
                      {ch.text}
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>

        {error && <div className="error-panel">{error}</div>}

        <button
          type="button"
          className="button button-primary button-full"
          onClick={() => void submit()}
          disabled={busy || answered === 0}
        >
          {busy ? 'Striking…' : `Strike (${answered}/${fight.questions.length} answered)`}
        </button>
      </div>
    )
  }

  // ── Aftermath ─────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="boss-page">
        <div className={result.passed ? 'boss-result boss-won' : 'boss-result boss-lost'}>
          <h1>{result.passed ? '🏆 Defeated!' : result.expired ? '⏱ Out of time' : '💀 It survives'}</h1>
          <p>
            {result.score} / {result.total} correct · {result.damage} damage dealt · boss left with{' '}
            {result.hpLeft} HP
          </p>
          {result.maxCombo > 2 && <p className="combo">🔥 best combo: {result.maxCombo}</p>}
          {result.xpGranted > 0 && <p className="xp-pop">+{result.xpGranted} XP · +50 💎</p>}
        </div>

        {result.artifact && (
          <div className={`artifact-drop rarity-${result.artifact.rarity}`}>
            <span className="artifact-label">Artifact acquired</span>
            <h2>{result.artifact.name}</h2>
            <p className="artifact-flavor">“{result.artifact.flavor_text}”</p>
            <Link to="/collection">See your collection →</Link>
          </div>
        )}

        <section className="zone">
          <h3 className="zone-heading">Review</h3>
          <ol className="questions">
            {result.feedback.map((f, i) => (
              <li key={f.questionId} className="question">
                <p className={f.correct ? 'explain explain-ok' : 'explain explain-no'}>
                  {f.correct ? '✅ ' : '❌ '}
                  <strong>Q{i + 1}.</strong> {f.explanation}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <div className="lesson-footer">
          <Link to={`/floor/${slug}`} className="button">Back to the floor</Link>
          {!result.passed && (
            <span className="dim">The boss recovers for 20 minutes before it can be challenged again.</span>
          )}
        </div>
      </div>
    )
  }

  // ── Pre-fight ─────────────────────────────────────────────────────────────
  return (
    <div className="boss-page">
      <nav className="breadcrumb">
        <Link to="/">Tower</Link>
        <span>/</span>
        <Link to={`/floor/${status.module.slug}`}>{status.module.zone}</Link>
      </nav>

      <header className="boss-header" style={{ borderColor: status.module.color }}>
        <h1>⚔ {status.module.boss}</h1>
        <p className="dim">Floor boss · {status.module.zone}</p>
      </header>

      {status.defeated && (
        <div className="result result-pass">
          <strong>Already defeated.</strong>
          <span>Best finish: {status.bestHpLeft} HP remaining</span>
        </div>
      )}

      <div className="boss-brief">
        <p>
          Eight questions drawn at random from everything on this floor. Ten minutes. The boss has
          100 HP and each correct answer lands for 16 — so a clean run alone will not finish it.
          <strong> Consecutive correct answers multiply your damage</strong>, and that is the only
          way to bring it down.
        </p>
        <ul>
          <li>The clock runs on the server; closing the tab does not pause it.</li>
          <li>Wrong answers deal nothing and reset the combo.</li>
          <li>Lose and the boss recovers for 20 minutes.</li>
        </ul>
      </div>

      {error && <div className="error-panel">{error}</div>}

      {status.eligible ? (
        <button type="button" className="button button-primary button-full" onClick={() => void start()} disabled={busy}>
          {busy ? 'Entering…' : status.defeated ? 'Fight again' : `Challenge ${status.module.boss}`}
        </button>
      ) : (
        <div className="notice">
          {status.reason === 'lessons_incomplete' && (
            <>
              Finish every lesson on this floor first — {status.lessonsDone} of{' '}
              {status.lessonsTotal} done. <Link to={`/floor/${status.module.slug}`}>Back to the floor →</Link>
            </>
          )}
          {status.reason === 'cooldown' && status.cooldownUntil && (
            <>The boss is recovering. Try again in about {Math.ceil((status.cooldownUntil - Date.now()) / 60000)} minutes.</>
          )}
          {status.reason === 'no_questions' && <>This floor has no questions yet.</>}
        </div>
      )}
    </div>
  )
}

/** Optimistic HP preview. The server recomputes it; this is only for feel. */
/**
 * The client's optimistic guess at damage, for the HP bar only. The server
 * recomputes everything at /submit — this cannot inflate a score, it can only
 * make the bar wrong for a moment.
 */
function estimateDamage(
  questions: Question[],
  selected: Record<string, string[]>,
  typed: Record<string, string>,
): number {
  let combo = 0
  let damage = 0
  for (const q of questions) {
    const hasAnswer =
      q.kind === 'predict'
        ? (typed[q.id] ?? '').trim().length > 0
        : (selected[q.id]?.length ?? 0) > 0
    if (hasAnswer) {
      combo++
      damage += Math.round(16 * (combo >= 6 ? 2 : combo >= 3 ? 1.5 : 1))
    } else {
      combo = 0
    }
  }
  return damage
}
